// The kraken: one of four beasts of the old charts, lit by the sonar, in a
// slot of its own above the terrain (ui-map-interface.md §6.4, R525).
//
// Round twenty-six measured why the old kraken read as nothing (R522): the
// creature slot sits before `terrain`, and the terrain program paints the
// trench floor over it at α 0.88, so the body composited to the value of
// bare floor. Round twenty-seven then pitched ten sea monsters (R523) and
// the owner picked four. They ship here, in the `kraken` slot that
// `LAYER_SLOTS` puts after `territory`: over the trench floor, under the
// hex views, the heat map and every unit. The whales stay under the
// terrain in `creatures.ts`, because §6.4 refuses a whale in front of rock.
//
// **The four.** Beast 0 is card 01 *Hic sunt dracones*: a hooded mantle out
// of a drawn waterline, eight arms hooking inward over it, two staring
// eyes. Beast 1 is card 02 *The breach*: a leaning dome cut at the surface,
// three arms that slap down, rings and spray. Beast 2 is card 06 *The deep
// rise*: a blur that sharpens into a dome with hanging arms and blurs
// again, and each sweep freezes a rim. Beast 3 is card 07 *The shadow under
// the keel*: a finned mantle that passes under the point with eight
// trailing arms. One `mulberry32` stream, seeded once, picks between them
// and never picks the same beast twice, so a session shows a roster.
//
// **What one beast costs**, at the middle of its life and with no sweep out:
//
//   beast 0  hood     2 sprites  170 segments   55 discs   5 rings  1 arc
//   beast 1  breach   2 sprites  124 segments   31 discs   7 rings  0 arcs
//   beast 2  dome     3 sprites  170 segments    6 discs   2 rings  0 arcs
//   beast 3  keel     3 sprites  193 segments    3 discs   1 ring   0 arcs
//
// A capsule run carries the edge instead of a third baked cell, so a beast
// is heavier than the mockup's cost line and lighter than a hull. One is on
// the map at a time, and the layer culls on its own extent.
//
// **THE ONE THING THIS FILE MUST GET RIGHT: the light runs across the
// animal, and it is the marine snow's rule** (R101, §7). A feature within
// `LIT_BAND_PX` of a ping front takes the front's strength, and it decays
// at `LIT_DECAY_PER_SEC`. Each feature reads the front AT ITS OWN POSITION,
// so a sweep lifts the near arm before the far one. A feature is a slot of
// one flat pool, and the pool is indexed by the order the beast draws in —
// the routines are fixed-count loops, so slot `i` is the same rim capsule,
// the same sucker or the same eye on every frame of one life.
//
// **The rim is not baked.** A body is two atlas cells over the loop set of
// `render/kraken-shapes.ts`: the mass and the engraving inside it. One
// baked cell takes one tint and one alpha, so the edge could not carry a
// running light; the layer therefore strokes the SAME loops as capsules.
// The two cells share one box, so they sit on one another.
//
// The figures a reader tunes — the two inks, the four alpha pairs, the rim
// width and how long each beast is — are `data/presentation/presentation.
// toml` `[presentation.creatures]`. The bezier coefficients, the arm angles
// and the wobble clocks are the DRAWING, and they stay the mockup's own
// literals here (`archive/mockups/027-kraken-chart.html`), in the card units
// the shape leaf is written in. Each routine states which card it is.

import {
  ACCENT_COLOR, CREATURE_KRAKEN_AMBIENT_ALPHA, CREATURE_KRAKEN_BEAST_PX,
  CREATURE_KRAKEN_BODY_ALPHA, CREATURE_KRAKEN_INK, CREATURE_KRAKEN_LIFE_SEC,
  CREATURE_KRAKEN_LIT_ALPHA, CREATURE_KRAKEN_MARK, CREATURE_KRAKEN_MARK_ALPHA,
  CREATURE_KRAKEN_MARK_LIT_ALPHA, CREATURE_KRAKEN_MAX_SEC, CREATURE_KRAKEN_MIN_SEC,
  CREATURE_KRAKEN_RIM_PX, LIT_BAND_PX, LIT_DECAY_PER_SEC, TERRAIN_TRENCH_RIM,
} from '../../gen/presentation';
import {
  BREACH_WATERLINE, HOOD_WATERLINE, KRAKEN_BODIES,
} from '../../render/kraken-shapes';
import type { PingFront } from '../../render/sonar-types';
import { ease } from '../../render/recipe-kit';
import { BLOCK_TRENCH, cellCenter, mulberry32 } from '../../world';
import type { ViewRect } from '../layer';
import type { AtlasEntry } from '../gfx/atlas';
import { F_ROUND, outside, type DrawCtx, type FrameInfo, type Layer } from '../layer';
import type { BatchSource, CircleSink, QuadSink, SegmentSink } from './batches';
import { cellsOf } from './shared';

/** How many beasts the roster holds. The `beast` knob names one of them. */
export const KRAKEN_BEASTS = KRAKEN_BODIES.length;

/** No sweep this frame. One shared empty array, so a quiet frame allocates nothing. */
const NO_FRONTS: readonly PingFront[] = [];

/** The trench rim blue: every edge of every beast, as the cards draw it. */
const RIM = TERRAIN_TRENCH_RIM.color;

/**
 * How many features one beast lights.
 *
 * The widest routine writes about two hundred, and a pool that is too short
 * would give two features one slot and make the light stutter.
 */
const FEATURES = 512;

/** The soft mass cell is baked over this square, in world px. */
const BLUR_CELL_PX = 256;

/**
 * How far past its own origin each beast reaches, in CARD units.
 *
 * The layer culls on its own extent: a waterline ring of card 01 runs to
 * 176 and the shadow of card 07 travels 190 either way, and a point test on
 * the origin would drop a beast that still covers the screen.
 */
const EXTENT_CARD = [200, 220, 170, 400] as const;

/** Card 02's three arms over the line: the root x, the heading, the side. */
const BREACH_ARMS: readonly (readonly [number, number, number])[] = [
  [-30, -Math.PI / 2 - 0.95, -1], [2, -Math.PI / 2 - 0.1, 1], [32, -Math.PI / 2 + 0.72, 1],
];

/** How many points one arm is sampled into. `curl` of the mockup's kit. */
const ARM_STEPS = 9;

/** The x of the card point `(px, py)` turned by `(ca, sa)` and scaled by
 *  `k` about the origin `ox`. A function and not a tuple, so a frame
 *  allocates nothing (§10). */
function turnX(ox: number, ca: number, sa: number, k: number, px: number, py: number): number {
  return ox + (ca * px - sa * py) * k;
}

/** The y of the same point, about the origin `oy`. */
function turnY(oy: number, ca: number, sa: number, k: number, px: number, py: number): number {
  return oy + (sa * px + ca * py) * k;
}

/** One body's two baked cells, resolved once at construction. */
interface BodyCells {
  mass: AtlasEntry;
  hatch: AtlasEntry;
}

/**
 * The trench cell the clock raises the beast in (R2626): the cell at
 * `start`, or with an `avoid` rule the first cell from `start` on, in the
 * order of the list and round its end, that the rule lets stand. -1 when
 * the rule takes every cell. The stream draws one number either way, so a
 * world with no rule picks the cell it always picked.
 */
export function pickTrench(
  trenches: Int32Array, start: number, avoid: ((cell: number) => boolean) | null,
): number {
  const n = trenches.length;
  if (n === 0) return -1;
  if (!avoid) return trenches[start];
  for (let k = 0; k < n; k++) {
    const c = trenches[(start + k) % n];
    if (!avoid(c)) return c;
  }
  return -1;
}

export class KrakenLayer implements Layer {
  readonly name = 'kraken';

  private readonly quads: QuadSink;
  private readonly circles: CircleSink;
  private readonly segments: SegmentSink;
  private readonly cells: BodyCells[] = [];
  private readonly blur: AtlasEntry;
  private shown = true;

  private trenches: Int32Array = new Int32Array(0);
  /** The blocked mask itself, for the debug key's open-floor fallback (R545). */
  private mask: Uint8Array | null = null;
  private x = 0;
  private y = 0;
  /** Seconds the beast has been up, or -1 while the trench is quiet. */
  private ageS = -1;
  /** Which of the four is up, or -1. */
  private which = -1;
  /** The last beast drawn, so the stream never repeats one. */
  private last = -1;
  private nextS = 0;
  private on = true;
  /** The `beast` knob: 0 draws at random, 1..4 forces one. */
  private forced = 0;
  /** Whether the beast draws its water: see `setWater`. */
  private water = true;

  /**
   * A trench cell the clock must not raise the beast in, or null for none
   * (R2606, R2626). The scene answers it: with `pirates` on, a cell the
   * survey of the viewer holds, because a monster of v2 §8.3 hunts in the
   * known abyss and the beast of the old charts keeps to unknown water.
   * The debug key ignores it.
   */
  avoid: ((cell: number) => boolean) | null = null;

  private rand = mulberry32(0x4b52a);
  private fronts: readonly PingFront[] = NO_FRONTS;

  /** The excitement of every feature, in the draw order of the routine. */
  private readonly light = new Float32Array(FEATURES);
  /** Which feature the routine is on. `lit` reads and advances it. */
  private fi = 0;
  /** The step the light decays over. `update` sets it before it packs. */
  private dt = 0;
  /** A group fade inside one routine — card 06 hides its body in the blur. */
  private fade = 1;

  /** One arm, as flat x, y pairs. `curl` fills it and the emitters read it. */
  private readonly pts = new Float32Array(64 * 2);

  /** How lit the body was last frame, so card 06 can freeze a rim. */
  private bodyLit = 0;
  /** Seconds since a front last crossed the body, or -1. */
  private crossedS = -1;
  /** How sharp card 06 was at that moment. */
  private frozen = 0;

  constructor(src: BatchSource) {
    // One beast is on the map at a time, so the batches are sized for the
    // widest routine and never grown.
    this.quads = src.newQuads(8);
    this.circles = src.newCircles(96);
    this.segments = src.newSegments(256);
    for (const b of KRAKEN_BODIES) {
      this.cells.push({
        mass: src.sprite(`kraken-${b.key}`),
        hatch: src.sprite(`kraken-${b.key}-hatch`),
      });
    }
    this.blur = src.sprite('kraken-blur');
    this.arm();
  }

  /**
   * The blocked mask of the running world.
   *
   * It rebuilds the trench list and drops a live beast: the world under it
   * is going away, and a beast left standing would hang over the new one.
   */
  setBlocked(mask: Uint8Array | null): void {
    this.trenches = cellsOf(mask, BLOCK_TRENCH);
    this.mask = mask;
    this.ageS = -1;
    this.which = -1;
    this.arm();
  }

  /**
   * The `kraken` and `beast` knobs (`fast/knobs.ts`).
   *
   * `kraken=0` takes the layer off and drops whatever is up. `beast=N`
   * forces one of the four for an eye test, and `0` keeps the draw random.
   */
  configure(on: boolean, beast = 0): void {
    if (on === this.on && beast === this.forced) return;
    this.on = on;
    this.forced = beast;
    if (!on) { this.ageS = -1; this.which = -1; }
  }

  /**
   * Whether a beast draws the water round the animal: the waterline rings,
   * the stain and the near line of card 01, the drowned half, the rings,
   * the wet edge and the spray of card 02, the soft mass and the frozen rim
   * of card 06, and the soft mass of card 07.
   *
   * The map always draws it. The Discord emoji `ap_kraken` turns it off,
   * because an emoji is the animal alone (brand.md §14.3). The animal
   * itself, its arms, its body and its face, draws the same either way.
   */
  setWater(on: boolean): void {
    this.water = on;
  }

  /**
   * The live sweeps this frame, as `SnowLayer.feed` takes them.
   *
   * The array is kept and never copied: `SonarLayer.fronts()` reuses its own
   * pool inside the frame it was asked in.
   */
  feed(fronts: readonly PingFront[]): void {
    this.fronts = fronts;
  }

  /**
   * Raises a beast now (the `.` debug key of §6.4).
   *
   * The knob is the umbrella: with the layer off, the key must not put one
   * on the map behind the reader's back.
   */
  summon(beast?: number, within?: ViewRect): void {
    if (!this.on) return;
    this.spawn(beast, within);
  }

  update(frame: FrameInfo): void {
    const dt = frame.dtMs / 1000;
    this.dt = dt;
    this.step(dt);
    this.pack(frame);
  }

  draw(ctx: DrawCtx): void {
    this.quads.flush(ctx.cam);
    this.segments.flush(ctx.cam);
    this.circles.flush(ctx.cam);
  }

  setVisible(v: boolean): void { this.shown = v; }
  get visible(): boolean { return this.shown; }

  /** True while a beast is up. The tests and the debug handle read it. */
  get up(): boolean { return this.ageS >= 0; }
  /** Which of the four is up, 0 to 3, or -1. */
  get beast(): number { return this.which; }
  /** Where it is, in world px. The headed check centres the camera on it. */
  get atX(): number { return this.x; }
  get atY(): number { return this.y; }
  /** How long it has been up, in seconds, or -1. */
  get age(): number { return this.ageS; }

  // -- the clocks ------------------------------------------------------------

  private arm(): void {
    this.nextS = CREATURE_KRAKEN_MIN_SEC
      + this.rand() * (CREATURE_KRAKEN_MAX_SEC - CREATURE_KRAKEN_MIN_SEC);
  }

  private step(dt: number): void {
    if (!this.on) return;
    if (this.ageS >= 0) {
      this.ageS += dt;
      if (this.crossedS >= 0) this.crossedS += dt;
      if (this.ageS > CREATURE_KRAKEN_LIFE_SEC) { this.ageS = -1; this.which = -1; }
      return;
    }
    this.nextS -= dt;
    if (this.nextS <= 0) this.spawn();
  }

  /**
   * Raises one beast in a random trench cell, or in the view (R545).
   *
   * The clock hands no `within`, and the beast rises in any trench of the
   * world. The debug key hands the visible world rect, so the owner sees
   * what they summoned: a trench cell in that rect first, and open floor in
   * it when the view holds no trench. A view off the world falls back to the
   * clock's own pick.
   *
   * The pick is the `beast` knob's when it states one, and otherwise the
   * stream's — and the stream draws from the three the last spawn was not,
   * so no two lives in a row show the same animal.
   */
  private spawn(beast?: number, within?: ViewRect): void {
    this.arm();
    let cell = within ? this.cellInView(within) : -1;
    if (cell < 0) {
      if (this.trenches.length === 0) return;
      cell = pickTrench(this.trenches, Math.floor(this.rand() * this.trenches.length), this.avoid);
      if (cell < 0) return;
    }
    const [x, y] = cellCenter(cell);
    this.x = x;
    this.y = y;
    const want = beast !== undefined ? beast : this.forced - 1;
    if (want >= 0 && want < KRAKEN_BEASTS) {
      this.which = want;
    } else if (this.last < 0) {
      this.which = Math.floor(this.rand() * KRAKEN_BEASTS);
    } else {
      const k = Math.floor(this.rand() * (KRAKEN_BEASTS - 1));
      this.which = k >= this.last ? k + 1 : k;
    }
    this.last = this.which;
    this.ageS = 0;
    // A new animal is a new set of features, and it must start dark.
    this.light.fill(0);
    this.bodyLit = 0;
    this.crossedS = -1;
  }

  /**
   * A cell whose centre lies in the rect: a trench when there is one, open
   * floor otherwise, and -1 when the rect holds neither (R545).
   *
   * A debug key is rare, so the scan is not cached: the trench list is
   * short, and the mask walk runs only when the view holds no trench.
   */
  private cellInView(r: ViewRect): number {
    const inside = (i: number): boolean => {
      const [x, y] = cellCenter(i);
      return x >= r.x0 && x <= r.x1 && y >= r.y0 && y <= r.y1;
    };
    const pick = (list: number[]): number =>
      list.length === 0 ? -1 : list[Math.floor(this.rand() * list.length)];
    const trench: number[] = [];
    for (let k = 0; k < this.trenches.length; k++) {
      if (inside(this.trenches[k])) trench.push(this.trenches[k]);
    }
    if (trench.length > 0) return pick(trench);
    const mask = this.mask;
    if (!mask) return -1;
    const floor: number[] = [];
    for (let i = 0; i < mask.length; i++) if (mask[i] === 0 && inside(i)) floor.push(i);
    return pick(floor);
  }

  // -- the light -------------------------------------------------------------

  /**
   * How lit the next feature is, and the raise and the decay that go with it.
   *
   * This is `SnowLayer.light` for one grain, inlined at the draw: the caller
   * hands the feature's own world position, the pool slot advances, and the
   * value the caller paints with is the one BEFORE this frame's decay, so a
   * feature the front is on paints at full strength.
   */
  private lit(x: number, y: number): number {
    const i = this.fi++;
    if (i >= FEATURES) return 0;
    const p = this.light;
    for (const w of this.fronts) {
      if (w.strength <= 0) continue;
      const d = Math.hypot(x - w.x, y - w.y);
      if (Math.abs(d - w.r) < LIT_BAND_PX && w.strength > p[i]) p[i] = w.strength;
    }
    const v = p[i];
    if (v > 0) p[i] = Math.max(0, v - LIT_DECAY_PER_SEC * this.dt);
    return v;
  }

  /** The rim alpha of a feature: the quiet figure, raised by the light. */
  private rimA(l: number): number {
    const a = CREATURE_KRAKEN_AMBIENT_ALPHA;
    return (a + (CREATURE_KRAKEN_LIT_ALPHA - a) * l) * this.fade;
  }

  /** The same for a mark — a sucker, a hatch plate, a glint, a drop. */
  private markA(l: number): number {
    const a = CREATURE_KRAKEN_MARK_ALPHA;
    return (a + (CREATURE_KRAKEN_MARK_LIT_ALPHA - a) * l) * this.fade;
  }

  // -- the kit ---------------------------------------------------------------

  /**
   * A curling arm, in world px. The heading turns harder toward the tip, so
   * the arm hooks instead of pointing (`curl` of the mockup's kit).
   */
  private curl(
    x0: number, y0: number, ang: number, len: number, curve: number,
  ): number {
    const p = this.pts;
    let x = x0, y = y0, a = ang;
    const step = len / ARM_STEPS;
    for (let k = 0; k <= ARM_STEPS; k++) {
      p[k * 2] = x;
      p[k * 2 + 1] = y;
      x += Math.cos(a) * step;
      y += Math.sin(a) * step;
      a += curve * (0.35 + (k / ARM_STEPS) * 2.2) / ARM_STEPS;
    }
    return ARM_STEPS + 1;
  }

  /** The lit edge pass of one arm: a capsule a step, each reading the front. */
  private limbRim(n: number, w0: number, w1: number): void {
    const p = this.pts;
    for (let k = 0; k < n - 1; k++) {
      const u = k / Math.max(1, n - 2);
      const x = p[k * 2], y = p[k * 2 + 1];
      this.segments.line(x, y, p[k * 2 + 2], p[k * 2 + 3],
        Math.max(0.6, w0 + (w1 - w0) * u), RIM, this.rimA(this.lit(x, y)), F_ROUND);
    }
  }

  /** The mass pass over it, which never lights. */
  private limbInk(n: number, w0: number, w1: number, alpha: number): void {
    const p = this.pts;
    for (let k = 0; k < n - 1; k++) {
      const u = k / Math.max(1, n - 2);
      this.segments.line(p[k * 2], p[k * 2 + 1], p[k * 2 + 2], p[k * 2 + 3],
        Math.max(0.6, w0 + (w1 - w0) * u), CREATURE_KRAKEN_INK,
        alpha * this.fade, F_ROUND);
    }
  }

  /** The mass and then the edge: one arm in the round's two inks. */
  private inkedLimb(n: number, w: number, taper: number, k: number): void {
    this.limbRim(n, w + 3.4 * k, taper + 2.6 * k);
    this.limbInk(n, w, taper, 0.9);
  }

  /** Sucker discs down one side of an arm. Each one reads the front alone. */
  private suckers(n: number, side: number, r0: number, from: number): void {
    const p = this.pts;
    for (let k = from; k < n - 1; k++) {
      const u = k / (n - 1);
      const x = p[k * 2], y = p[k * 2 + 1];
      const dx = p[k * 2 + 2] - x, dy = p[k * 2 + 3] - y;
      const l = Math.hypot(dx, dy) || 1;
      const r = Math.max(0.55, r0 * (1 - u * 0.65));
      this.circles.disc(x - dy / l * side * r * 1.15, y + dx / l * side * r * 1.15,
        r, CREATURE_KRAKEN_MARK, this.markA(this.lit(x, y)) * 0.92);
    }
  }

  /**
   * A staring eye of the old charts: a dark lid, an accent iris, a slit
   * pupil across it, a glint and a rim (`stareEye` of the mockup's kit).
   *
   * The mockup clips an almond lid and draws an ellipse pupil. A batch has
   * neither, so the lid is a disc and the slit is one capsule laid across
   * the iris — which is the part that makes the eye read at 0.5×.
   */
  private eye(x: number, y: number, r: number, gaze: number): void {
    const l = this.lit(x, y);
    const ink = CREATURE_KRAKEN_INK;
    this.circles.disc(x, y, r * 1.35, ink, 0.9 * this.fade);
    const gx = x + Math.cos(gaze) * r * 0.32;
    const gy = y + Math.sin(gaze) * r * 0.18;
    this.circles.disc(gx, gy, r * 0.86, ACCENT_COLOR,
      Math.min(1, 0.34 + 0.62 * l) * this.fade);
    const sa = gaze + Math.PI / 2, hl = r * 0.7;
    this.segments.line(gx - Math.cos(sa) * hl, gy - Math.sin(sa) * hl,
      gx + Math.cos(sa) * hl, gy + Math.sin(sa) * hl,
      Math.max(0.8, r * 0.44), ink, 0.96 * this.fade, F_ROUND);
    this.circles.disc(gx - r * 0.36, gy - r * 0.36, r * 0.22,
      CREATURE_KRAKEN_MARK, this.markA(l));
    this.circles.ring(x, y, r * 1.35, Math.max(1.2, r * 0.28), RIM, this.rimA(l));
  }

  /** A quadratic run of capsules: a brow, a wet edge, a lash. */
  private quadRun(
    x0: number, y0: number, cx: number, cy: number, x1: number, y1: number,
    steps: number, width: number, color: number, alpha: number | null,
  ): void {
    let ax = x0, ay = y0;
    for (let k = 1; k <= steps; k++) {
      const u = k / steps, v = 1 - u;
      const bx = v * v * x0 + 2 * v * u * cx + u * u * x1;
      const by = v * v * y0 + 2 * v * u * cy + u * u * y1;
      const a = alpha === null
        ? this.rimA(this.lit((ax + bx) / 2, (ay + by) / 2))
        : alpha * this.fade;
      this.segments.line(ax, ay, bx, by, width, color, a, F_ROUND);
      ax = bx; ay = by;
    }
  }

  /** The brow ridge and the two staring eyes of a chart beast (`face`). */
  private face(
    ox: number, oy: number, k: number, y: number, dx: number, r: number,
    gazeL: number, gazeR: number,
  ): void {
    // The brow runs from eye to eye over the face; the card point (px, py)
    // stands at (ox + px * k, oy + py * k).
    const browY = oy + (y - r * 1.3) * k;
    this.quadRun(ox - dx * 1.9 * k, browY, ox, oy + (y - r * 3.4) * k,
      ox + dx * 1.9 * k, browY, 4, 2.2 * k, RIM, null);
    this.eye(ox - dx * k, oy + y * k, r * k, gazeL);
    this.eye(ox + dx * k, oy + y * k, r * k, gazeR);
  }

  /**
   * The mass and the engraving of one body, then its edge as capsules.
   *
   * The two cells share one box, so both are drawn origin-relative and the
   * hatching lands on the mantle. The rim walks the loop set the cells were
   * baked from, so one curve carries both.
   */
  private body(
    b: number, ox: number, oy: number, rot: number, k: number, hatchBase: number,
  ): void {
    const cell = this.cells[b];
    const plan = KRAKEN_BODIES[b].hatch;
    const ca = Math.cos(rot), sa = Math.sin(rot);
    const hx = turnX(ox, ca, sa, k, plan.cx, plan.cy);
    const hy = turnY(oy, ca, sa, k, plan.cx, plan.cy);
    const l = this.lit(hx, hy);
    this.bodyLit = l;
    this.quads.sprite(ox, oy, rot, cell.mass, CREATURE_KRAKEN_INK,
      CREATURE_KRAKEN_BODY_ALPHA * this.fade, k, 0, false);
    this.quads.sprite(ox, oy, rot, cell.hatch, CREATURE_KRAKEN_MARK,
      hatchBase * (0.36 + 1.5 * l) * this.fade, k, 0, false);
    const w = CREATURE_KRAKEN_RIM_PX * k;
    for (const loop of KRAKEN_BODIES[b].loops) {
      const n = loop.length / 2;
      let px = turnX(ox, ca, sa, k, loop[0], loop[1]);
      let py = turnY(oy, ca, sa, k, loop[0], loop[1]);
      for (let i = 1; i <= n; i++) {
        const j = (i % n) * 2;
        const qx = turnX(ox, ca, sa, k, loop[j], loop[j + 1]);
        const qy = turnY(oy, ca, sa, k, loop[j], loop[j + 1]);
        this.segments.line(px, py, qx, qy, w, RIM,
          this.rimA(this.lit((px + qx) / 2, (py + qy) / 2)), F_ROUND);
        px = qx; py = qy;
      }
    }
  }

  // -- the frame -------------------------------------------------------------

  private pack(frame: FrameInfo): void {
    const q = this.quads, c = this.circles, s = this.segments;
    q.begin();
    c.begin();
    s.begin();
    this.fi = 0;
    this.fade = 1;
    if (this.ageS < 0 || this.which < 0) return;
    const b = this.which;
    const k = CREATURE_KRAKEN_BEAST_PX[b] / KRAKEN_BODIES[b].cardLen;
    const pad = EXTENT_CARD[b] * k;
    const cull = frame.cull;
    if (outside(this.x, this.y, cull.x0 - pad, cull.y0 - pad, cull.x1 + pad, cull.y1 + pad)) {
      return;
    }
    // The life ramp of the layer, eased so the middle of the life holds
    // fuller (`life` of the mockup). It rides the batch fade, so the whole
    // animal comes up and goes down in one uniform.
    const vis = Math.pow(Math.sin(Math.PI * (this.ageS / CREATURE_KRAKEN_LIFE_SEC)), 0.6);
    q.globalAlpha = vis;
    c.globalAlpha = vis;
    s.globalAlpha = vis;
    const t = this.ageS;
    if (b === 0) this.hood(t, k);
    else if (b === 1) this.breach(t, k);
    else if (b === 2) this.rise(t, k);
    else this.keel(t, k);
  }

  // -- card 01, Hic sunt dracones --------------------------------------------

  /**
   * A hooded mantle rises out of a drawn waterline, two eyes stare out from
   * under a brow, and eight arms come up from behind the head and hook
   * inward over it.
   *
   * The four inner arms pass IN FRONT of the mantle, so they are drawn
   * after the body. The pool order is the same on every frame, because both
   * passes walk the same eight in the same order.
   */
  private hood(t: number, k: number): void {
    const ox = this.x, oy = this.y;
    const wl = oy + HOOD_WATERLINE * k;
    if (this.water) {
      // The drawn waterline of an old chart: three flat rings it rises from.
      for (let j = 0; j < 3; j++) {
        const u = (t * 0.32 + j / 3) % 1;
        const l = this.lit(ox, wl);
        this.circles.ring(ox, wl, (58 + u * 118) * k, 1, RIM,
          (0.16 + 0.5 * l) * (1 - u) * 0.9 * this.fade);
      }
      this.circles.disc(ox, oy + (HOOD_WATERLINE + 28) * k, 56 * k,
        CREATURE_KRAKEN_INK, 0.22 * this.fade);
    }
    for (let pass = 0; pass < 2; pass++) {
      for (let i = 0; i < 8; i++) {
        const kk = i % 4;
        if ((kk === 0 ? 1 : 0) !== pass) continue;
        const side = i < 4 ? -1 : 1;
        const wob = Math.sin(t * 0.8 + i * 1.4) * 0.14;
        const ang = -Math.PI / 2 + side * (0.38 + kk * 0.3) + wob;
        const n = this.curl(
          ox + side * (20 + kk * 7) * k, oy + (HOOD_WATERLINE - 4 - kk * 5) * k,
          ang, (72 + kk * 9) * k, -side * 1.9);
        this.inkedLimb(n, 9 * k, 1.8 * k, k);
        this.suckers(n, -side, 2 * k, 3);
      }
      if (pass === 0) {
        this.body(0, ox, oy, 0, k, 0.26);
        this.face(ox, oy, k, -22, 14, 7.6, 2.9, 0.25);
      }
    }
    // The near half of the waterline passes in front of the body.
    if (this.water) {
      this.circles.arc(ox, wl, 58 * k, 1.4 * k, 0, Math.PI, RIM,
        (0.3 + 0.55 * this.lit(ox, wl)) * this.fade);
    }
  }

  // -- card 02, The breach ----------------------------------------------------

  /**
   * The animal comes up leaning, and the waterline cuts it: the dome, the
   * eyes and three arms are above the line, and what is under it is a dim
   * shadow. The arms slap down on a slow clock and the surface answers with
   * rings and spray.
   *
   * The lean and the cut are baked into the body's loop (R525,
   * `render/kraken-shapes.ts`), so the sprite is drawn straight and the
   * three arms are turned by the same angle here. `curl` turns with its own
   * start and heading, so one rotation of two numbers does it.
   */
  private breach(t: number, k: number): void {
    const ox = this.x, oy = this.y;
    const wl = oy + BREACH_WATERLINE * k;
    const slap = Math.sin(t * 0.7);
    const ink = CREATURE_KRAKEN_INK;
    if (this.water) {
      // The drowned half: a stain and five limp arms, all of them dim.
      this.circles.disc(ox - 6 * k, oy + (BREACH_WATERLINE + 36) * k, 72 * k, ink, 0.24 * this.fade);
      for (let i = 0; i < 5; i++) {
        const n = this.curl(ox + (-40 + i * 20) * k, oy + (BREACH_WATERLINE + 14) * k,
          0.9 + i * 0.35, 60 * k, 1);
        this.limbInk(n, 7 * k, 1.6 * k, 0.18);
      }
      for (let j = 0; j < 5; j++) {
        const u = (t * 0.28 + j / 5) % 1;
        const rx = (40 + u * 150) * k;
        this.circles.ring(ox, wl, rx, 1.1 * k, RIM,
          (0.18 + 0.5 * this.lit(ox + rx * 0.7, wl)) * (1 - u) * this.fade);
      }
    }
    const rot = -0.2, ca = Math.cos(rot), sa = Math.sin(rot);
    for (let i = 0; i < 3; i++) {
      const a = BREACH_ARMS[i];
      const ang = a[1] + slap * 0.3 * (i === 1 ? 1 : -0.7) + rot;
      const bx = a[0], by = BREACH_WATERLINE - 6;
      const sx = ox + (ca * bx - sa * by - 8) * k;
      const sy = oy + (sa * bx + ca * by) * k;
      const n = this.curl(sx, sy, ang, (98 + i * 4) * k, -a[2] * 1.6);
      this.inkedLimb(n, 10 * k, 2 * k, k);
      this.suckers(n, -a[2], 2.2 * k, 3);
    }
    this.body(1, ox, oy, 0, k, 0.24);
    this.face(ox, oy, k, -14, 15, 7.4, 2.7, 0.35);
    if (!this.water) return;
    // The wet edge, the band the light finds hardest.
    this.quadRun(ox - 46 * k, wl, ox - 6 * k, wl - 8 * k, ox + 40 * k, wl,
      4, 2.8 * k, CREATURE_KRAKEN_MARK, null);
    for (let j = 0; j < 6; j++) {
      const ph = (t * 0.7 / (2 * Math.PI) + j / 6) % 1;
      const h = Math.sin(ph * Math.PI);
      const x = ox + (-60 + j * 24 + (j % 2) * 6) * k;
      const y = wl - (4 + h * (10 + (j % 3) * 6)) * k;
      this.circles.disc(x, y, (1.1 + 0.5 * (j % 2)) * k, CREATURE_KRAKEN_MARK,
        this.markA(this.lit(x, y)) * (1 - ph * 0.6));
    }
  }

  // -- card 06, The deep rise -------------------------------------------------

  /**
   * The animal starts as a soft dark mass with no edge, sharpens and
   * shrinks into a hard silhouette with arms hanging under it, and sinks
   * back into the blur. A mass with no outline has no size, which is the
   * menace of the card.
   *
   * Each sweep freezes the sharpness the animal had when the front crossed
   * it, and the frozen rim hangs in the water for 2.6 s while the beast
   * keeps moving. The crossing is read off the body's own light: the pool
   * slot rises only when a front is on it.
   */
  private rise(t: number, k: number): void {
    const ox = this.x, oy = this.y;
    // How sharp the edge is: up over the first half, down over the last.
    const u = t / CREATURE_KRAKEN_LIFE_SEC;
    const sh = ease((u - 0.16) / 0.28) * (1 - ease((u - 0.62) / 0.3));
    const r = (92 - 42 * sh) * k;
    if (this.water) {
      this.quads.sprite(ox, oy, 0, this.blur, CREATURE_KRAKEN_INK,
        0.3 + 0.28 * (1 - sh), (r * 2) / BLUR_CELL_PX);
    }
    // The body fades into the blur as the edge goes, and the arms with it.
    this.fade = Math.max(0.1, sh);
    for (let i = 0; i < 8; i++) {
      const a = Math.PI / 2 + (i - 3.5) * 0.34 + Math.sin(t * 0.9 + i) * 0.14;
      const n = this.curl(ox + Math.cos(a) * 22 * k, oy + (16 + Math.sin(a) * 8) * k,
        a, (66 + (i % 3) * 8) * k, i > 3.5 ? -1.5 : 1.5);
      this.inkedLimb(n, 8 * k, 1.7 * k, k);
    }
    const was = this.bodyLit;
    this.body(2, ox, oy, 0, k, 0.24);
    this.face(ox, oy, k, -6, 12, 6.8, 2.9, 0.25);
    this.fade = 1;
    if (this.bodyLit > was) { this.crossedS = 0; this.frozen = sh; }
    if (this.water && this.crossedS >= 0 && this.crossedS < 2.6) {
      this.circles.ring(ox, oy - 4 * k, (92 - 46 * this.frozen) * k, 2 * k, RIM,
        0.55 * (1 - this.crossedS / 2.6));
    }
  }

  // -- card 07, The shadow under the keel -------------------------------------

  /**
   * A finned silhouette crosses under the point, the mantle first, the head
   * behind it and eight arms trailing. Between sweeps it is a stain a
   * player can argue away, and the sweep draws its outline for a moment.
   *
   * The whole animal is turned by its pitch, so the arms are turned with
   * the body: `curl` takes the turned start and the turned heading.
   */
  private keel(t: number, k: number): void {
    const u = t / CREATURE_KRAKEN_LIFE_SEC;
    const ox = this.x + (-190 + u * 380) * k;
    const oy = this.y + Math.sin(t * 0.34) * 10 * k;
    const rot = Math.sin(t * 0.34) * 0.08;
    const ca = Math.cos(rot), sa = Math.sin(rot);
    if (this.water) {
      this.quads.sprite(ox, oy, 0, this.blur, CREATURE_KRAKEN_INK,
        0.3, (112 * 2 * k) / BLUR_CELL_PX);
    }
    const hx = -26;
    for (let i = 0; i < 8; i++) {
      const s = i - 3.5;
      const a = Math.PI + s * 0.27 + Math.sin(t + i) * 0.12 + rot;
      const bx = hx - 6, by = s * 8;
      const n = this.curl(turnX(ox, ca, sa, k, bx, by), turnY(oy, ca, sa, k, bx, by),
        a, (84 + (i % 2) * 14) * k, s * 0.55);
      this.limbInk(n, 17 * k, 2.6 * k, 0.84);
      this.limbRim(n, 7 * k, 1.4 * k);
    }
    this.body(3, ox, oy, rot, k, 0.2);
    // One brow over the single eye that faces the reader.
    this.quadRun(
      turnX(ox, ca, sa, k, hx - 16, -14), turnY(oy, ca, sa, k, hx - 16, -14),
      turnX(ox, ca, sa, k, hx + 4, -30), turnY(oy, ca, sa, k, hx + 4, -30),
      turnX(ox, ca, sa, k, hx + 20, -8), turnY(oy, ca, sa, k, hx + 20, -8),
      3, 2.2 * k, RIM, null);
    this.eye(turnX(ox, ca, sa, k, hx + 2, -2), turnY(oy, ca, sa, k, hx + 2, -2), 10 * k, 0.1);
  }
}
